"use client";

import Link from "next/link";
import { ComponentPropsWithoutRef, FC, ReactNode } from "react";
import { useMessages } from "../context/i18n";
import { rivalPage as enRivalPage } from "../dictionaries/en/rival-page";
import Typography from "./Typography";

export type ToolRow = {
  name: string;
  href?: string;
  startsAt: string;
  freeOption: string;
  // What a plan is metered in.
  counts: string;
  posting: string;
  api: string;
  highlight?: boolean;
};

export type ToolsTableData = {
  title: ReactNode;
  subtitle?: ReactNode;
  rows: ToolRow[];
  caption: ReactNode;
};

type AlternativeToolsTableProps = {
  data: ToolsTableData;
} & ComponentPropsWithoutRef<"section">;

const AlternativeToolsTable: FC<AlternativeToolsTableProps> = ({
  data,
  ...rest
}) => {
  const { columns } = useMessages("rivalPage", enRivalPage);
  return (
  <section {...rest}>
    <Typography variant="h2" component="h2" className="text-center">
      {data.title}
    </Typography>
    {data.subtitle && (
      <Typography
        variant="body1"
        className="mt-4 text-slate-600 text-center max-w-3xl mx-auto"
      >
        {data.subtitle}
      </Typography>
    )}
    <div className="mt-12 overflow-x-auto rounded-2xl border border-slate-200">
      <table className="min-w-[56rem] w-full bg-white text-left text-sm">
        <thead>
          <tr className="border-b border-slate-200">
            {Object.values(columns).map((column) => (
              <th key={column} scope="col" className="p-4 font-semibold">
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((row) => (
            <tr
              key={row.name}
              className={
                row.highlight ? "bg-violet-50 font-medium" : "even:bg-slate-50"
              }
            >
              <th scope="row" className="p-4 font-semibold">
                {row.href ? (
                  <Link
                    href={row.href}
                    className="text-violet-600 underline underline-offset-2"
                  >
                    {row.name}
                  </Link>
                ) : (
                  row.name
                )}
              </th>
              <td className="p-4 text-slate-700">{row.startsAt}</td>
              <td className="p-4 text-slate-700">{row.freeOption}</td>
              <td className="p-4 text-slate-700">{row.counts}</td>
              <td className="p-4 text-slate-700">{row.posting}</td>
              <td className="p-4 text-slate-700">{row.api}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <Typography
      variant="caption"
      className="block mx-auto text-slate-600 text-center mt-4"
    >
      {data.caption}
    </Typography>
  </section>
  );
};

export default AlternativeToolsTable;
